import { useEffect, useRef } from "react";
import toast from "react-hot-toast";
import {
  connectLeadRealtime,
  isLeadRealtimeForUser,
  LeadRealtimePayload,
  shouldRefreshLeadTable,
  subscribeLeadTableRefresh,
} from "../notifications/leadRealtime";

type Options = {
  userId?: string | null;
  /** Silent table refetch — must NOT flip full-page loading. */
  onSilentRefresh: () => void;
  /** Show toast on this page for lead events */
  showToast?: boolean;
  enabled?: boolean;
};

/**
 * Keeps My / All / Team lead tables in sync via Socket.IO without full-page reload.
 */
export function useLeadRealtimeRefresh({
  userId,
  onSilentRefresh,
  showToast = true,
  enabled = true,
}: Options) {
  const refreshRef = useRef(onSilentRefresh);
  refreshRef.current = onSilentRefresh;
  const lastToastAt = useRef(0);
  const lastRefreshAt = useRef(0);

  useEffect(() => {
    if (!enabled || !userId) return;

    connectLeadRealtime(String(userId));

    const unsub = subscribeLeadTableRefresh((payload: LeadRealtimePayload) => {
      if (!isLeadRealtimeForUser(payload, String(userId))) return;
      if (!shouldRefreshLeadTable(payload)) return;
      if (document.visibilityState !== "visible") return;

      // Debounce silent refreshes so stacked events don't thrash filters/UI
      const now = Date.now();
      if (now - lastRefreshAt.current < 8000) return;
      lastRefreshAt.current = now;

      refreshRef.current();

      if (showToast) {
        // Debounce stacked assign events
        if (now - lastToastAt.current < 1200) return;
        lastToastAt.current = now;
        const title = payload.title || "Lead update";
        const body = payload.body || payload.text || "";
        toast(
          <div style={{ fontSize: 12 }}>
            <div style={{ fontWeight: 600 }}>{title}</div>
            {body ? <div>{body}</div> : null}
          </div>,
          { duration: 4500, position: "top-right" }
        );
      }
    });

    return () => {
      unsub();
    };
  }, [userId, enabled, showToast]);
}
